import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import elementSettings from 'src/js/modules/element-settings';
import useTemporaryInlineStyles from 'src/js/modules/breakpoint-editing/hooks/use-temporary-inline-styles';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';

const { Paragraph } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { SliderControl },
  constants: { targetClasses, breakpointKeys },
} = elementSettings;

const isMobile = bowser.mobile || bowser.tablet;

const SLIDER_MIN = 0;
const SLIDER_MAX = 80;
const SLIDER_STEP = 1;
const RESTRICTED_VALUE = 30;

const TARGET_TO_TEMPORARY_STYLES = {
  [targetClasses.COLS_GAP_X]: (value) => ({
    'column-gap': value,
    '--ws-columns-column-gap': value,
  }),
};

const parseValueWithUnit = (value) => `${parseFloat(value)}px`;

const getLiveElements = (elementIds) =>
  elementIds.map((elementId) => view.accessors.getLiveElement(elementId)).filter(Boolean);

const getValuesByBreakpoint = (value, isBreakpointEditingEnabled, activeBreakpointKey) => {
  if (isBreakpointEditingEnabled) {
    return { [activeBreakpointKey]: value };
  }

  return {
    [breakpointKeys.DEFAULT]: value,
    [breakpointKeys.MAX_XL]:
      parseFloat(value) > RESTRICTED_VALUE ? parseValueWithUnit(RESTRICTED_VALUE) : undefined,
  };
};

const getPopoverProps = ({ imgSrc, title }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}
    </Paragraph>
  ),
});

const getValuePopoverProps = (restrictedValue) => ({
  renderContent: () => {
    const desktopMessage = i18next.t(
      'Note: Gap over {value} will be scaled down on tablets and mobile devices.',
      { value: parseValueWithUnit(restrictedValue) }
    );
    const mobileMessage = i18next.t('Note: Gap over {value} will only be shown on desktop.', {
      value: parseValueWithUnit(restrictedValue),
    });

    return (
      <Paragraph appearance="white" size="small">
        {isMobile ? mobileMessage : desktopMessage}
      </Paragraph>
    );
  },
  checkVisibility: (value) => value > restrictedValue,
});

function HorizontalGapControl({ elementIds, targets, onChange }) {
  const { deviceMode } = useContext(ElementSettingsContext);
  const isBreakpointEditingEnabled = Boolean(deviceMode?.active);

  const { setTemporaryStyles, restoreTemporaryStyles } = useTemporaryInlineStyles();

  const valuePopover = useMemo(
    () => (isBreakpointEditingEnabled ? null : getValuePopoverProps(RESTRICTED_VALUE)),
    [isBreakpointEditingEnabled]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, targets, value, skipTracking }) => {
      const parsedValue = parseValueWithUnit(value);

      setTemporaryStyles({
        elements: getLiveElements(elementIds),
        styles: Object.assign(
          {},
          ...targets.map((target) => TARGET_TO_TEMPORARY_STYLES[target](parsedValue))
        ),
      });

      onChange({
        controlId,
        elementIds,
        targets,
        value: parsedValue,
        skipTracking,
        skipApplyChanges: true,
        shouldScrollToTarget: false,
      });
    },
    [onChange, setTemporaryStyles]
  );

  const handleAfterChange = useCallback(
    ({ controlId, elementIds, targets, value }) => {
      restoreTemporaryStyles({ elements: getLiveElements(elementIds) });

      onChange({
        controlId,
        elementIds,
        targets,
        value: getValuesByBreakpoint(
          parseValueWithUnit(value),
          isBreakpointEditingEnabled,
          deviceMode.breakpointKey
        ),
      });
    },
    [deviceMode, isBreakpointEditingEnabled, onChange, restoreTemporaryStyles]
  );

  return (
    <SliderControl
      id={controlIds.HORIZONTAL_GAP}
      elementIds={elementIds}
      targets={targets}
      title={i18next.t('Horizontal gap')}
      iconGlyph="gap-vertical"
      mixedLabel={i18next.t('Mixed')}
      step={SLIDER_STEP}
      min={SLIDER_MIN}
      max={SLIDER_MAX}
      popover={getPopoverProps({
        imgSrc: previewGifs.COLUMNS_HORIZONTAL_GAP_GIF,
        title: i18next.t('Adjust the horizontal spacing between columns'),
      })}
      {...(valuePopover && { valuePopover })}
      computeFallbackValue={computeSpacingFallback}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
    />
  );
}

HorizontalGapControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default HorizontalGapControl;
